import { z } from 'zod/v4';
import type { MastraBase } from '../../../base';
import type { MastraLLMVNext } from '../../../llm/model/model.loop';
import type { Mastra } from '../../../mastra';
import type {
  ErrorProcessorOrWorkflow,
  InputProcessorOrWorkflow,
  LLMRequestProcessorOrWorkflow,
  OutputProcessorOrWorkflow,
} from '../../../processors';
import type { RequestContext } from '../../../request-context';
import type { Agent } from '../../agent';
import type { AgentExecuteOnFinishOptions } from '../../types';

export type AgentCapabilities = {
  agent: Agent<any, any, any, any>;
  agentName: string;
  logger: MastraBase['logger'];
  getMemory: Agent['getMemory'];
  getModel: Agent['getModel'];
  generateMessageId: Mastra['generateId'];
  mastra?: Mastra;
  _agentNetworkAppend?: boolean;
  convertTools: Agent['convertTools'];
  runInputProcessors: Agent['__runInputProcessors'];
  executeOnFinish: (args: AgentExecuteOnFinishOptions) => Promise<void>;
  outputProcessors?:
    | OutputProcessorOrWorkflow[]
    | ((args: {
        requestContext: RequestContext;
        overrides?: OutputProcessorOrWorkflow[];
      }) => Promise<OutputProcessorOrWorkflow[]> | OutputProcessorOrWorkflow[]);
  inputProcessors?:
    | InputProcessorOrWorkflow[]
    | ((args: {
        requestContext: RequestContext;
        overrides?: InputProcessorOrWorkflow[];
      }) => Promise<InputProcessorOrWorkflow[]> | InputProcessorOrWorkflow[]);
  llmRequestInputProcessors?:
    | LLMRequestProcessorOrWorkflow[]
    | ((args: {
        requestContext: RequestContext;
        overrides?: InputProcessorOrWorkflow[];
        errorOverrides?: ErrorProcessorOrWorkflow[];
      }) => Promise<LLMRequestProcessorOrWorkflow[]> | LLMRequestProcessorOrWorkflow[]);
  /**
   * The function form resolves the run's error processors once and reports whether the caller
   * configured any themselves (excluding framework defaults).
   */
  errorProcessors?:
    | ErrorProcessorOrWorkflow[]
    | ((args: {
        requestContext: RequestContext;
        overrides?: ErrorProcessorOrWorkflow[];
      }) =>
        | Promise<{ errorProcessors: ErrorProcessorOrWorkflow[]; hasConfiguredErrorProcessors: boolean }>
        | { errorProcessors: ErrorProcessorOrWorkflow[]; hasConfiguredErrorProcessors: boolean });
  llm: MastraLLMVNext;
};

export type CoreTool = {
  parameters: any;
  id?: string | undefined;
  description?: string | undefined;
  outputSchema?: any;
  execute?: (inputData: any, context: any) => any;
  toModelOutput?: (output: any) => any;
  type?: 'function' | 'provider-defined' | undefined;
  args?: Record<string, any> | undefined;
};

export const storageThreadSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  resourceId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  metadata: z.record(z.string(), z.any()).optional(),
});

// Step outputs in the evented prepare-stream workflow only carry JSON-safe
// markers — class instances (MessageList, Tools) and Maps (processorStates) live
// on the factory-closure runScope instead. See ./run-scope.ts.
export const prepareToolsStepOutputSchema = z.object({});

export const prepareMemoryStepOutputSchema = z.object({
  threadExists: z.boolean(),
  thread: storageThreadSchema.optional(),
  /** Tripwire data when input processor triggered abort */
  tripwire: z
    .object({
      reason: z.string(),
      retry: z.boolean().optional(),
      metadata: z.unknown().optional(),
      processorId: z.string().optional(),
    })
    .optional(),
});

export type PrepareMemoryStepOutput = z.infer<typeof prepareMemoryStepOutputSchema>;
export type PrepareToolsStepOutput = z.infer<typeof prepareToolsStepOutputSchema>;
